import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import apiClient from '../stateManagers/services/axiosInterceptor';
import { useClassesStore } from '../stateManagers/store/useClassesStore';
import { fileDownloadService } from '../apiSetup/services/FileDownloadService';
import { useDefaultDataStore } from '../stateManagers/store/useDefaultDataStore';
import { useSchoolDetailsStore } from '../stateManagers/newStores/useSchoolDetailsStore';

interface ClassItem {
  id: number;
  class_name: string;
  class_order: number;
  sections: string[] | null;
  created_at: string;
  updated_at: string;
}

interface NewClassRow {
  class_name: string;
  sections: string;
  order?: number;
}

export default function SetupClassesAndSections() {
  const { classes, loading, fetchClasses } = useClassesStore();
  const activeSchool = useDefaultDataStore(state => state.active_school);
  const { schoolDetails } = useSchoolDetailsStore();

  const [uploading, setUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState<{ class_name: string; sections: string[] }[]>([]);
  const [fileSelected, setFileSelected] = useState(false);

  const [newClasses, setNewClasses] = useState<NewClassRow[]>([
    { class_name: '', sections: '', order: undefined },
  ]);
  const [adding, setAdding] = useState(false);

  const [editingIds, setEditingIds] = useState<Set<number>>(new Set());
  const [editValues, setEditValues] = useState<Record<number, { class_name: string; sections: string; classOrder: number }>>({});
  const [savingEdits, setSavingEdits] = useState(false);

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const currentSession = schoolDetails?.current_session?.session || '';
  const currentSessionId = schoolDetails?.current_session?.id || '';

  useEffect(() => {
    fetchClassesWithSchool();
  }, [activeSchool?.school_id, currentSessionId]);

  const fetchClassesWithSchool = async () => {
    const schoolId = activeSchool?.school_id;
    if (schoolId && currentSessionId) {
      await fetchClasses(schoolId, currentSessionId);
    } else if (schoolId) {
      await fetchClasses(schoolId);
    } else {
      await fetchClasses();
    }
  };

  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    if (!file) { setFileSelected(false); return; }
    setFileSelected(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        toast.current?.show({ severity: 'error', summary: 'Invalid CSV', detail: 'CSV must contain at least a header and one data row', life: 3000 });
        return;
      }

      const previewData: { class_name: string; sections: string[] }[] = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const match = line.match(/^"?([^",]+)"?,\s*"([^"]+)"/);
        if (match) {
          previewData.push({ class_name: match[1].trim(), sections: match[2].split(',').map(s => s.trim()) });
        } else {
          const parts = line.split(',');
          if (parts.length >= 2) {
            previewData.push({
              class_name: parts[0].trim().replace(/"/g, ''),
              sections: parts.slice(1).join(',').trim().replace(/"/g, '').split(',').map(s => s.trim()),
            });
          } else if (parts.length === 1 && parts[0].trim()) {
            previewData.push({
              class_name: parts[0].trim().replace(/"/g, ''),
              sections: [],
            });
          }
        }
      }

      setCsvPreview(previewData);
      toast.current?.show({ severity: 'info', summary: 'Preview Ready', detail: `${previewData.length} classes found in CSV`, life: 3000 });
    };
    reader.readAsText(file);
  };

  const handleFileUpload = async (event: any) => {
    const file = event.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('csv_file', file);
    if (activeSchool?.school_id) {
      formData.append('school_id', activeSchool.school_id);
    }
    if (currentSessionId) {
      formData.append('session_id', currentSessionId);
    }
    if (currentSession) {
      formData.append('session', currentSession);
    }

    try {
      setUploading(true);
      const response = await apiClient.post('/classes/bulk', formData);

      if (Array.isArray(response.data)) {
        const successCount = response.data.filter((item: any) => item.status === 'success').length;
        const errorCount = response.data.filter((item: any) => item.status === 'error').length;

        if (successCount > 0) {
          toast.current?.show({ severity: 'success', summary: 'Upload Complete', detail: `${successCount} class(es) added!${errorCount > 0 ? ` ${errorCount} failed.` : ''}`, life: 4000 });
          setCsvPreview([]); setFileSelected(false);
          fileUploadRef.current?.clear();
          setTimeout(() => fetchClassesWithSchool(), 500);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: 'No classes were added. Check your CSV.', life: 3000 });
        }

        if (errorCount > 0) {
          const errors = response.data.filter((i: any) => i.status === 'error').map((i: any) => `${i.class_name || 'Unknown'}: ${i.message}`).join(', ');
          toast.current?.show({ severity: 'warn', summary: 'Some Errors', detail: errors.length > 100 ? errors.substring(0, 100) + '...' : errors, life: 5000 });
        }
      } else if (response.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Upload Success', detail: 'Classes uploaded successfully!', life: 3000 });
        setCsvPreview([]); setFileSelected(false);
        fileUploadRef.current?.clear();
        setTimeout(() => fetchClassesWithSchool(), 500);
      }
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: 'Upload Failed', detail: error.response?.data?.message || 'Failed to upload CSV file', life: 3000 });
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = async () => {
    await fileDownloadService.downloadFile({
      filename: 'classes.csv',
      onSuccess: (filename) => toast.current?.show({ severity: 'success', summary: 'Download Complete', detail: `${filename} downloaded successfully`, life: 3000 }),
      onError: (error) => toast.current?.show({ severity: 'error', summary: 'Download Failed', detail: error.response?.data?.error || 'Failed to download file', life: 3000 }),
      onProgress: (progress) => console.log(`Download progress: ${progress}%`),
    });
  };

  const handleAddRow = () => {
    setNewClasses((prev) => [...prev, { class_name: '', sections: '', order: undefined }]);
  };

  const handleRemoveRow = (index: number) => {
    setNewClasses((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleUpdateNewRow = (index: number, field: 'class_name' | 'sections' | 'order', value: string) => {
    setNewClasses((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        if (field === 'order') {
          const parsed = value === '' ? undefined : Number(value);
          return { ...row, order: Number.isNaN(parsed as number) ? row.order : parsed };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleSaveNewClasses = async () => {
    if (!currentSessionId) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Current session not found. Please ensure a session is marked as "Current".', life: 3000 });
      return;
    }

    const trimmed = newClasses.map((row) => ({
      class_name: row.class_name.trim(),
      sections: row.sections.split(',').map(s => s.trim()).filter(Boolean),
      order: row.order,
    }));

    // Sections are optional now — only class name is required.
    const invalid = trimmed.some((row) => !row.class_name);
    if (invalid) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Fields', detail: 'Class name is required for every row.', life: 3000 });
      return;
    }

    setAdding(true);
    try {
      const results = await Promise.allSettled(
        trimmed.map((row) => {
          const payload: any = {
            class_name: row.class_name,
            session_id: currentSessionId,
            sections: row.sections,
          };
          if (row.order !== undefined) payload.class_order = row.order;
          if (activeSchool?.school_id) payload.school_id = activeSchool.school_id;
          return apiClient.post('/classes', payload);
        })
      );

      const successCount = results.filter((r) => r.status === 'fulfilled' && (r.value as any).data?.status === 'success').length;
      const errorCount = results.length - successCount;

      if (successCount > 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Added',
          detail: `${successCount} class(es) created successfully.${errorCount > 0 ? ` ${errorCount} failed.` : ''}`,
          life: 3500,
        });
        setNewClasses([{ class_name: '', sections: '', order: undefined }]);
        fetchClassesWithSchool();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'No classes were created. Please check the rows.', life: 3000 });
      }

      if (errorCount > 0) {
        const messages = results
          .map((r) => {
            if (r.status === 'rejected') return r.reason?.response?.data?.message || 'Request failed';
            if (r.value.data?.status === 'error') return r.value.data?.message || 'Could not add class';
            return null;
          })
          .filter(Boolean)
          .slice(0, 3)
          .join('; ');
        toast.current?.show({ severity: 'warn', summary: 'Some Errors Occurred', detail: messages.length > 150 ? messages.substring(0, 150) + '...' : messages, life: 5000 });
      }
    } finally {
      setAdding(false);
    }
  };

  const startEditing = (list: ClassItem[]) => {
    setEditingIds(new Set(list.map((c) => c.id)));
    setEditValues((prev) => {
      const next = { ...prev };
      list.forEach((c) => {
        next[c.id] = {
          class_name: c.class_name,
          sections: (c.sections ?? []).join(', '),
          classOrder: c.class_order,
        };
      });
      return next;
    });
  };

  const cancelEditing = () => {
    setEditingIds(new Set());
  };

  const updateEditField = (id: number, field: 'class_name' | 'sections' | 'classOrder', value: string) => {
    setEditValues((prev) => {
      const current = prev[id];
      if (!current) return prev;
      if (field === 'classOrder') {
        const parsed = value === '' ? current.classOrder : Number(value);
        return { ...prev, [id]: { ...current, classOrder: Number.isNaN(parsed) ? current.classOrder : parsed } };
      }
      return { ...prev, [id]: { ...current, [field]: value } };
    });
  };

  const handleSaveEdits = async () => {
    const targets = classes.filter((c: ClassItem) => editingIds.has(c.id));
    const trimmed = targets.map((c: ClassItem) => {
      const edit = editValues[c.id];
      return {
        class_id: c.id,
        class_name: (edit?.class_name ?? c.class_name).trim(),
        sections: (edit?.sections ?? (c.sections ?? []).join(', ')).split(',').map((s) => s.trim()).filter(Boolean),
        class_order: edit?.classOrder ?? c.class_order,
      };
    });

    // Sections are optional now — only class name is required.
    const invalid = trimmed.some((row) => !row.class_name);
    if (invalid) {
      toast.current?.show({ severity: 'warn', summary: 'Missing Fields', detail: 'Class name is required.', life: 3000 });
      return;
    }

    setSavingEdits(true);
    try {
      const results = await Promise.allSettled(
        trimmed.map((row) => {
          const payload: any = {
            class_id: row.class_id,
            class_name: row.class_name,
            sections: row.sections,
            class_order: row.class_order,
          };
          if (activeSchool?.school_id) payload.school_id = activeSchool.school_id;
          return apiClient.put('/classes', payload);
        })
      );

      const successCount = results.filter((r) => r.status === 'fulfilled' && (r.value as any).data?.status === 'success').length;
      const errorCount = results.length - successCount;

      if (successCount > 0) {
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: `${successCount} class(es) updated successfully.${errorCount > 0 ? ` ${errorCount} failed.` : ''}`, life: 3000 });
      }
      if (errorCount > 0) {
        const messages = results
          .map((r) => {
            if (r.status === 'rejected') return r.reason?.response?.data?.message || 'Request failed';
            if (r.value.data?.status === 'error') return r.value.data?.message || 'Could not update class';
            return null;
          })
          .filter(Boolean)
          .slice(0, 3)
          .join('; ');
        toast.current?.show({
          severity: 'warn',
          summary: 'Some Errors Occurred',
          detail: messages.length > 150 ? messages.substring(0, 150) + '...' : messages,
          life: 5000,
        });
        if (errorCount === trimmed.length) {
          setSavingEdits(false);
          return;
        }
      }

      setEditingIds(new Set());
      await fetchClassesWithSchool();
    } finally {
      setSavingEdits(false);
    }
  };

  const handleDeleteClass = (classItem: ClassItem) => {
    confirmDialog({
      message: `Are you sure you want to delete "${classItem.class_name}"? This action cannot be undone.`,
      header: 'Delete Class',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          setDeletingId(classItem.id);
          const response = await apiClient.delete(`/classes/${classItem.id}`);

          if (response.data.status === 'success') {
            toast.current?.show({ severity: 'success', summary: 'Deleted', detail: `"${classItem.class_name}" deleted successfully`, life: 3000 });
            await fetchClassesWithSchool();
          } else {
            toast.current?.show({ severity: 'error', summary: 'Failed', detail: response.data.message || 'Could not delete class', life: 3000 });
          }
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to delete class', life: 3000 });
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fdfdfd' }}>
        <div style={{ textAlign: 'center' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#2855ae' }} />
          <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>Loading classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfd' }}>
      <Toast ref={toast} />
      <ConfirmDialog />

      <style>{`
        @keyframes fadeInSlide {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes bulkBarIn { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }
        .custom-file-upload .p-fileupload-buttonbar {
          background: transparent !important; border: none !important; padding: 0 !important;
          display: flex !important; gap: 12px !important; justify-content: center !important;
        }
        .custom-file-upload .p-button {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
          border: none !important; border-radius: 12px !important; padding: 10px 20px !important;
          font-size: 14px !important; font-weight: 600 !important;
          box-shadow: 0 2px 8px rgba(59,130,246,0.3) !important; transition: all 0.2s !important;
        }
        .custom-file-upload .p-fileupload-content {
          background: #f9fafb !important; border: 2px dashed #e5e7eb !important;
          border-radius: 12px !important; padding: 20px !important; margin-top: 12px !important;
        }
        .p-confirm-dialog .p-dialog-content { display: flex; align-items: flex-start; gap: 12px; }
        .edit-action-bar {
          animation: bulkBarIn .22s ease;
          display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px;
          background: linear-gradient(135deg,#0f766e,#14b8a6);
          border-radius: 14px; padding: 12px 18px; margin-bottom: 16px;
          box-shadow: 0 4px 16px rgba(20,184,166,.3);
        }
        .edit-action-btn {
          display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border-radius:10px;
          font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all .15s;
        }
        .edit-action-btn:disabled { opacity: .5; cursor: not-allowed; }
        .edit-action-btn.primary { background:white; color:#0f766e; }
        .edit-action-btn.primary:hover:not(:disabled) { background:#f0fdfa; }
        .edit-action-btn.ghost { background:rgba(255,255,255,.15); color:white; border:1px solid rgba(255,255,255,.3); }
        .edit-action-btn.ghost:hover:not(:disabled) { background:rgba(255,255,255,.25); }
      `}</style>

      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: '24px', padding: '32px', marginBottom: '24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            background: 'rgba(255,255,255,0.2)', borderRadius: '16px', padding: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="pi pi-book" style={{ fontSize: '18px', color: 'white' }} />
          </div>
          <div>
            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Setup Classes and Sections
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                background: 'rgba(74,222,128,0.9)', color: 'white', padding: '4px 12px',
                borderRadius: '12px', fontSize: '12px', fontWeight: '600',
              }}>
                {classes.length} Classes
              </span>
              {currentSession && (
                <span style={{
                  background: 'rgba(251,191,36,0.9)', color: 'white', padding: '4px 12px',
                  borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                }}>
                  Session: {currentSession}
                </span>
              )}
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                {new Date().toISOString().split('T')[0]}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '20px', padding: '28px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', padding: '8px', minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-plus" style={{ fontSize: '14px', color: 'white' }} />
          </div>
          <h3 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: '0' }}>Add New Classes</h3>
        </div>

        {!currentSessionId && (
          <div style={{ background: '#fef3c7', borderRadius: '8px', padding: '12px', border: '1px solid #fcd34d', marginBottom: '16px' }}>
            <p style={{ color: '#92400e', fontSize: '13px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-exclamation-triangle" style={{ color: '#92400e' }} />
              No current session found. Please ensure a session is marked as "Current" in Session Management.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {newClasses.map((row, index) => {
            const sectionsPreview = row.sections.split(',').map(s => s.trim()).filter(Boolean);
            return (
              <div
                key={index}
                style={{ background: '#f9fafb', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                <div style={{ minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                  {index + 1}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Class Name *</label>
                      <InputText
                        value={row.class_name}
                        onChange={(e) => handleUpdateNewRow(index, 'class_name', e.target.value)}
                        placeholder="e.g., Grade 1"
                        disabled={adding}
                        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ flex: '2', minWidth: '260px' }}>
                      <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Sections (optional)</label>
                      <InputText
                        value={row.sections}
                        onChange={(e) => handleUpdateNewRow(index, 'sections', e.target.value)}
                        placeholder="e.g., Section A, Section B, Section C"
                        disabled={adding}
                        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ flex: '0 0 100px', minWidth: '100px' }}>
                      <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Order</label>
                      <InputText
                        type="number"
                        value={row.order === undefined ? '' : String(row.order)}
                        onChange={(e) => handleUpdateNewRow(index, 'order', e.target.value)}
                        placeholder="auto"
                        disabled={adding}
                        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                  {sectionsPreview.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {sectionsPreview.map((s, i) => (
                        <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveRow(index)}
                  disabled={newClasses.length === 1 || adding}
                  style={{
                    background: newClasses.length === 1 ? '#e5e7eb' : '#ef4444', border: 'none', borderRadius: '8px',
                    width: '32px', height: '32px', cursor: newClasses.length === 1 ? 'not-allowed' : 'pointer', color: 'white', flexShrink: 0,
                  }}
                >
                  <i className="pi pi-trash" style={{ fontSize: '12px' }} />
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleAddRow}
            disabled={adding}
            style={{
              background: 'white', border: '2px solid #3b82f6', borderRadius: '12px', padding: '10px 20px',
              cursor: adding ? 'not-allowed' : 'pointer', color: '#3b82f6', fontSize: '14px', fontWeight: '600',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <i className="pi pi-plus" /> Add Another
          </button>
          <button
            onClick={handleSaveNewClasses}
            disabled={adding || !currentSessionId}
            style={{
              background: adding || !currentSessionId ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none',
              borderRadius: '12px', padding: '10px 24px', cursor: adding || !currentSessionId ? 'not-allowed' : 'pointer', color: 'white',
              fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            {adding ? 'Saving...' : 'Save Classes'}
          </button>
        </div>
      </div>

      {classes.length === 0 && (
        <>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #dbeafe',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px',
                padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                minWidth: '32px', height: '32px',
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }} />
              </div>
              <div>
                <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>
                  CSV Format Instructions
                </h4>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
                  Upload a CSV file with columns: <strong>class_name, sections</strong>.
                  Sections should be comma-separated within quotes (optional). Example: "Section A, Section B, Section C"
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'white', borderRadius: '20px', padding: '32px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '50%',
                width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <i className="pi pi-upload" style={{ fontSize: '32px', color: 'white' }} />
              </div>
              <h3 style={{ color: '#2855ae', fontSize: '20px', margin: '0 0 8px 0', fontWeight: '600' }}>
                Upload Classes CSV File
              </h3>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                Drag and drop your CSV file here or click to browse
              </p>
            </div>

            <div className="custom-file-upload">
              <FileUpload
                ref={fileUploadRef}
                name="csv_file"
                accept=".csv"
                maxFileSize={5000000}
                customUpload
                uploadHandler={handleFileUpload}
                onSelect={handleFileSelect}
                onClear={() => { setFileSelected(false); setCsvPreview([]); }}
                onRemove={() => { setFileSelected(false); setCsvPreview([]); }}
                disabled={uploading}
                chooseLabel="Choose CSV File"
                uploadLabel="Upload"
                cancelLabel="Clear"
                emptyTemplate={
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '10px', margin: 0, fontSize: '13px' }}>
                    Drag and drop CSV file here to upload.
                  </p>
                }
              />
            </div>

            {csvPreview.length > 0 && (
              <div style={{
                marginTop: '24px', background: '#f9fafb', borderRadius: '12px',
                padding: '20px', border: '1px solid #e5e7eb', animation: 'fadeInSlide 0.5s ease-out',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h4 style={{ color: '#2855ae', fontSize: '16px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="pi pi-eye" style={{ fontSize: '16px' }} />
                    CSV Preview ({csvPreview.length} classes)
                  </h4>
                  <button
                    onClick={() => { setCsvPreview([]); setFileSelected(false); fileUploadRef.current?.clear(); }}
                    style={{
                      background: '#ef4444', border: 'none', borderRadius: '8px', padding: '6px 12px',
                      cursor: 'pointer', color: 'white', fontSize: '12px', fontWeight: '600',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    <i className="pi pi-times" style={{ fontSize: '12px' }} />
                    Clear Preview
                  </button>
                </div>
                <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {csvPreview.map((item, index) => (
                    <div key={index} style={{
                      background: 'white', borderRadius: '10px', padding: '16px',
                      border: '1px solid #e5e7eb', animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s backwards`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '8px',
                          width: '32px', height: '32px', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: '600',
                        }}>{index + 1}</div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: 'block', color: '#6b7280', fontSize: '10px', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>Class Name</label>
                          <p style={{ margin: 0, color: '#2855ae', fontSize: '14px', fontWeight: '600' }}>{item.class_name}</p>
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', color: '#6b7280', fontSize: '10px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase' }}>
                          Sections ({item.sections.length})
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {item.sections.length > 0
                            ? item.sections.map((section, idx) => (
                                <span key={idx} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                                  {section}
                                </span>
                              ))
                            : <span style={{ color: '#9ca3af', fontSize: '12px', fontStyle: 'italic' }}>No sections</span>
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                onClick={downloadSampleCSV}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none',
                  borderRadius: '12px', padding: '10px 20px', cursor: 'pointer', color: 'white',
                  fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center',
                  gap: '8px', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                }}
              >
                <i className="pi pi-download" style={{ fontSize: '14px' }} />
                Download Sample CSV
              </button>
            </div>
          </div>

          {!fileSelected && (
            <div style={{
              background: 'white', borderRadius: '20px', padding: '48px', textAlign: 'center',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)', animation: 'fadeInSlide 0.5s ease-out',
            }}>
              <i className="pi pi-inbox" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
              <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Classes Found</h3>
              <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
                Upload a CSV file or use "Add New Classes" above to get started.
              </p>
            </div>
          )}
        </>
      )}

      {editingIds.size > 0 && (
        <div className="edit-action-bar">
          <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>
            Editing {editingIds.size} class{editingIds.size !== 1 ? 'es' : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="edit-action-btn primary" disabled={savingEdits} onClick={handleSaveEdits}>
              {savingEdits ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 12 }} /> : <i className="pi pi-check" style={{ fontSize: 12 }} />}
              {savingEdits ? 'Saving...' : 'Save Changes'}
            </button>
            <button className="edit-action-btn ghost" disabled={savingEdits} onClick={cancelEditing}>
              <i className="pi pi-times" style={{ fontSize: 12 }} /> Cancel
            </button>
          </div>
        </div>
      )}

      {classes.length > 0 && (
        <>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #dbeafe', animation: 'fadeInSlide 0.5s ease-out',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px',
                padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                minWidth: '32px', height: '32px',
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }} />
              </div>
              <div>
                <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>Classes Configuration</h4>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
                  Your classes are configured below. Click the edit icon on a card to update it inline, or add new ones using "Add New Classes" above.
                </p>
              </div>
            </div>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '24px', marginBottom: '24px',
          }}>
            {classes.map((classItem: ClassItem, index: number) => {
              const isEditing = editingIds.has(classItem.id);
              const edit = editValues[classItem.id];

              if (isEditing) {
                const sectionsPreview = (edit?.sections ?? '').split(',').map(s => s.trim()).filter(Boolean);
                return (
                  <div key={classItem.id} style={{
                    background: '#f0fdfa', borderRadius: '20px', padding: '24px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #2dd4bf',
                    gridColumn: '1 / -1',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ minWidth: '40px', height: '40px', background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          <div style={{ flex: '1', minWidth: '200px' }}>
                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Class Name *</label>
                            <InputText
                              value={edit?.class_name ?? classItem.class_name}
                              onChange={(e) => updateEditField(classItem.id, 'class_name', e.target.value)}
                              disabled={savingEdits}
                              style={{ width: '100%', border: '1px solid #2dd4bf', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ flex: '2', minWidth: '260px' }}>
                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Sections (optional)</label>
                            <InputText
                              value={edit?.sections ?? (classItem.sections ?? []).join(', ')}
                              onChange={(e) => updateEditField(classItem.id, 'sections', e.target.value)}
                              disabled={savingEdits}
                              style={{ width: '100%', border: '1px solid #2dd4bf', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                            />
                          </div>
                          <div style={{ flex: '0 0 100px', minWidth: '100px' }}>
                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Order</label>
                            <InputText
                              type="number"
                              value={String(edit?.classOrder ?? classItem.class_order)}
                              onChange={(e) => updateEditField(classItem.id, 'classOrder', e.target.value)}
                              disabled={savingEdits}
                              style={{ width: '100%', border: '1px solid #2dd4bf', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}
                            />
                          </div>
                        </div>
                        {sectionsPreview.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {sectionsPreview.map((s, i) => (
                              <span key={i} style={{ background: '#ccfbf1', color: '#0f766e', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={handleSaveEdits}
                          disabled={savingEdits}
                          title="Save"
                          style={{ background: '#0f766e', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: savingEdits ? 'not-allowed' : 'pointer', color: 'white' }}
                        >
                          {savingEdits ? <i className="pi pi-spin pi-spinner" style={{ fontSize: 12 }} /> : <i className="pi pi-check" style={{ fontSize: 12 }} />}
                        </button>
                        <button
                          onClick={cancelEditing}
                          disabled={savingEdits}
                          title="Cancel"
                          style={{ background: '#e5e7eb', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: savingEdits ? 'not-allowed' : 'pointer', color: '#4b5563' }}
                        >
                          <i className="pi pi-times" style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={classItem.id} style={{
                  background: 'white', borderRadius: '20px', padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)', position: 'relative',
                  border: '2px solid #f1f5f9', animation: `fadeInSlide 0.5s ease-out ${index * 0.1}s backwards`,
                  opacity: deletingId === classItem.id ? 0.5 : 1, transition: 'opacity 0.2s',
                }}>
                  <div style={{ position: 'absolute', top: '20px', right: '20px', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => startEditing([classItem])}
                      disabled={deletingId === classItem.id}
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none',
                        borderRadius: '10px', width: '32px', height: '32px', cursor: 'pointer',
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                      }}
                      onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                      onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <i className="pi pi-pencil" style={{ fontSize: '14px' }} />
                    </button>

                    <button
                      onClick={() => handleDeleteClass(classItem)}
                      disabled={deletingId === classItem.id}
                      style={{
                        background: deletingId === classItem.id ? '#fca5a5' : '#ef4444',
                        border: 'none', borderRadius: '10px', width: '32px', height: '32px',
                        cursor: deletingId === classItem.id ? 'not-allowed' : 'pointer',
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(239,68,68,0.3)',
                      }}
                      onMouseOver={e => { if (deletingId !== classItem.id) e.currentTarget.style.transform = 'scale(1.1)'; }}
                      onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      {deletingId === classItem.id
                        ? <i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }} />
                        : <i className="pi pi-trash" style={{ fontSize: '14px' }} />
                      }
                    </button>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', marginRight: '80px' }}>
                    <div style={{
                      minWidth: '36px', height: '36px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: '600', fontSize: '13px', flexShrink: 0,
                    }}>
                      {classItem.class_order ?? index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', color: '#2855ae', fontSize: '11px', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Class Name
                      </label>
                      <InputText
                        value={classItem.class_name}
                        disabled
                        style={{ width: '100%', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '8px 12px', fontWeight: '600', color: '#2855ae', background: '#f9fafb' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', color: '#6b7280', fontSize: '11px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Sections ({classItem.sections?.length || 0})
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {classItem.sections && classItem.sections.length > 0
                        ? classItem.sections.map((section, idx) => (
                            <span key={idx} style={{ background: '#e0f2fe', color: '#0369a1', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600' }}>
                              {section}
                            </span>
                          ))
                        : <span style={{ color: '#9ca3af', fontSize: '12px', fontStyle: 'italic' }}>No sections available</span>
                      }
                    </div>
                  </div>

                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af' }}>
                    <span>Created: {new Date(classItem.created_at).toLocaleDateString()}</span>
                    <span>Updated: {new Date(classItem.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}